import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const IncomeTaxCalculatorPage = () => {
  const [income, setIncome] = useState('');
  const [filingStatus, setFilingStatus] = useState('single');
  const [deductions, setDeductions] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const grossIncome = parseFloat(income);
    const ded = parseFloat(deductions) || 0;
    const taxableIncome = Math.max(0, grossIncome - ded);

    // 2024 Tax Brackets (simplified)
    const brackets = filingStatus === 'single' 
      ? [[11600, 0.10], [47150, 0.12], [100525, 0.22], [191950, 0.24], [243725, 0.32], [609350, 0.35], [Infinity, 0.37]]
      : [[23200, 0.10], [94300, 0.12], [201050, 0.22], [383900, 0.24], [487450, 0.32], [731200, 0.35], [Infinity, 0.37]];

    let tax = 0;
    let prevLimit = 0;
    for (const [limit, rate] of brackets) {
      if (taxableIncome > prevLimit) {
        const taxableInBracket = Math.min(taxableIncome - prevLimit, limit - prevLimit);
        tax += taxableInBracket * rate;
      }
      if (taxableIncome <= limit) break;
      prevLimit = limit;
    }

    const effectiveRate = (tax / grossIncome) * 100;
    const afterTax = grossIncome - tax;

    setResult({ tax: tax.toFixed(2), effectiveRate: effectiveRate.toFixed(2), afterTax: afterTax.toFixed(2), taxableIncome: taxableIncome.toFixed(2) });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Income Tax Calculator</span>
          </nav>
          <h1 className="text-3xl font-bold text-white mb-2">Income Tax Calculator</h1>
          <p className="text-gray-400 mb-8">Estimate your federal income tax based on 2024 tax brackets.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-300 mb-2 font-medium">Gross Annual Income</label>
                <div className="flex gap-2 items-center">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={income} onChange={(e) => setIncome(e.target.value)} className="flex-1 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="75000" />
                </div>
              </div>
              <div>
                <label className="block text-gray-300 mb-2 font-medium">Filing Status</label>
                <select value={filingStatus} onChange={(e) => setFilingStatus(e.target.value)} className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg text-white">
                  <option value="single">Single</option>
                  <option value="married">Married Filing Jointly</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 mb-2 font-medium">Deductions (Standard: ${filingStatus === 'single' ? '14,600' : '29,200'})</label>
                <div className="flex gap-2 items-center">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={deductions} onChange={(e) => setDeductions(e.target.value)} className="flex-1 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder={filingStatus === 'single' ? '14600' : '29200'} />
                </div>
              </div>
            </div>
            <Button onClick={calculate} className="mt-5 bg-cyan-600 hover:bg-cyan-500 text-white">Calculate</Button>
            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <span className="text-gray-400 text-sm">Estimated Federal Tax</span>
                    <p className="text-2xl font-bold text-red-400">${parseFloat(result.tax).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <span className="text-gray-400 text-sm">After-Tax Income</span>
                    <p className="text-2xl font-bold text-green-400">${parseFloat(result.afterTax).toLocaleString()}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg"><span className="text-gray-500 text-sm">Taxable Income</span><p className="font-semibold text-white">${parseFloat(result.taxableIncome).toLocaleString()}</p></div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg"><span className="text-gray-500 text-sm">Effective Tax Rate</span><p className="font-semibold text-white">{result.effectiveRate}%</p></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default IncomeTaxCalculatorPage;
